<ng-template let-extraClass="extraClass" #topNav>
  @if (!isRightSideContent) {
    <tidy-top-nav
      [title]="isInquiry ? 'Inquiry' : 'Reservation'"
      [class]="extraClass"
      [canGoBack]="true" />
  }
</ng-template>

<ng-container
*ngTemplateOutlet="topNav;context:{ extraClass: 'mobile' }" />

<ng-template #content>
  <tidy-wrapper>
    <ng-container
    *ngTemplateOutlet="topNav;context:{ extraClass: 'desktop' }" />
    @if (loaded) {
      @if (isInquiry && inquiryView; as iv) {
        <div class="inqv">
          <tidy-card class="inqv-card">
            <div class="inqv-head">
              <div class="inqv-avatar">{{ iv.initials }}</div>
              <div class="inqv-head-meta">
                <tidy-text [header]="iv.guestName" />
                <tidy-text [helper]="iv.subtitle" />
              </div>
              <span [class]="'inqv-status inqv-status--' + iv.statusTone">
                {{ iv.statusLabel }}
              </span>
            </div>
            @if (iv.needsResponse) {
              <div class="inqv-banner">
                <span class="inqv-banner-icon-wrap">
                  <tidy-image [src]="'assets/svg/chatbubble-outline.svg'" class="inqv-banner-icon" />
                </span>
                <div class="inqv-banner-text">
                  <span class="inqv-banner-title">{{ iv.bannerTitle }}</span>
                  <span class="inqv-banner-hint">{{ iv.bannerHint }}</span>
                </div>
              </div>
            }
            @if (iv.chargeFailedNote) {
              <div class="inqv-banner">
                <span class="inqv-banner-icon-wrap">
                  <tidy-image [src]="'assets/svg/card-outline.svg'" class="inqv-banner-icon" />
                </span>
                <div class="inqv-banner-text">
                  <span class="inqv-banner-title">{{ iv.chargeFailedNote }}</span>
                </div>
              </div>
            }

            <div class="resv-stay">
              <div class="resv-stay-col">
                <span class="resv-stay-label">
                  <tidy-image [src]="'assets/svg/log-in-outline.svg'" class="resv-stay-label-icon" />
                  Check-in
                </span>
                <span class="resv-stay-date">{{ iv.checkIn.weekday }}, {{ iv.checkIn.date }}</span>
              </div>
              <div class="resv-stay-nights">
                <span class="resv-stay-track"></span>
                @if (iv.nightsLabel) {
                  <span class="resv-stay-nights-pill">
                    <tidy-image [src]="'assets/svg/moon-outline.svg'" class="resv-stay-nights-icon" />
                    {{ iv.nightsLabel }}
                  </span>
                }
                <span class="resv-stay-track"></span>
              </div>
              <div class="resv-stay-col resv-stay-col--right">
                <span class="resv-stay-label">
                  Check-out
                  <tidy-image [src]="'assets/svg/log-out-outline.svg'" class="resv-stay-label-icon" />
                </span>
                <span class="resv-stay-date">{{ iv.checkOut.weekday }}, {{ iv.checkOut.date }}</span>
              </div>
            </div>

            <div class="inqv-pills">
              @if (iv.guestsLabel) {
                <span class="inqv-pill">
                  <tidy-image [src]="'assets/svg/people-outline.svg'" class="inqv-pill-icon" />
                  {{ iv.guestsLabel }}
                </span>
              }
              <span class="inqv-pill">
                <tidy-image [src]="'assets/svg/home-outline.svg'" class="inqv-pill-icon" />
                {{ iv.propertyLine }}
              </span>
              @if (iv.expiresLabel) {
                <span class="inqv-expires">{{ iv.expiresLabel }}</span>
              }
              @if (iv.statusDetail) {
                <span class="inqv-expires inqv-expires--past">{{ iv.statusDetail }}</span>
              }
            </div>
            @if (iv.message) {
              <div class="inqv-message">
                <tidy-text [helper]="iv.message" />
              </div>
            }
          </tidy-card>
          @if (iv.totalLabel) {
            <tidy-card class="inqv-card">
              <div class="resv-section-head">
                <span class="resv-section-icon-tile">
                  <tidy-image [src]="'assets/svg/pricetag-outline.svg'" class="resv-section-icon" />
                </span>
                <div class="resv-section-copy">
                  <span class="resv-section-title">{{ iv.payoutTitle }}</span>
                  <span class="resv-section-hint">{{ iv.payoutHint }}</span>
                </div>
              </div>
              @if (iv.breakdownRows.length) {
                <div class="inqv-breakdown">
                  @for (row of iv.breakdownRows; track $index) {
                    <div class="inqv-breakdown-row">
                      <span class="inqv-breakdown-label">{{ row.label }}</span>
                      <span class="inqv-breakdown-value">{{ row.value }}</span>
                    </div>
                  }
                </div>
              }
              <div class="inqv-total-row">
                <span class="inqv-total-label">Total</span>
                <span class="inqv-total-value">{{ iv.totalLabel }}</span>
              </div>
            </tidy-card>
          }
          @if (iv.canAct || iv.platformUrl) {
            <div class="inqv-actions">
              <!-- Always present when the inquiry has a channel URL, including
                   alongside Decline / Pre-approve. This page is now the only
                   place a host can act on an inquiry, and the guest's message
                   often lives only on the channel — the payload frequently
                   carries no `message` at all — so the way to read the thread
                   cannot depend on the row having run out of actions. -->
              @if (iv.platformUrl) {
                <tidy-text
                  [actionBody]="iv.platformLinkText"
                  (action)="openInquiryPlatform()"
                  class="white-link" />
              }
              @if (iv.canDecline) {
                <tidy-small-button
                  [text]="'Decline'"
                  [disabled]="inquiryBusy"
                  (buttonClick)="openInquiryDecline()" />
              }
              @if (iv.canPreapprove) {
                <tidy-small-button
                  [text]="inquiryBusy ? 'Sending…' : 'Pre-approve'"
                  [primary]="true"
                  [disabled]="inquiryBusy"
                  (buttonClick)="preapproveInquiry()" />
              }
              @if (iv.canApprove) {
                <tidy-small-button
                  [text]="inquiryBusy ? 'Charging…' : iv.acceptLabel"
                  [primary]="true"
                  [disabled]="inquiryBusy"
                  (buttonClick)="approveInquiryWithCharge()" />
              }
            </div>
          }
        </div>
      } @else if (reservationView; as rv) {
        <div class="inqv resv">
          <tidy-card class="inqv-card">
            <div class="inqv-head">
              <div class="inqv-avatar">{{ rv.initials }}</div>
              <div class="inqv-head-meta">
                <tidy-text [header]="rv.guestName" />
                <tidy-text [helper]="rv.subtitle" />
                @if (rv.isHold) {
                  <span class="inqv-status inqv-status--hold">
                    {{ 'Hold: awaiting guest confirmation' | translate }}
                  </span>
                }
              </div>
              @if (canMessageGuest) {
                <tidy-pill-button
                  [text]="'Message Guest'"
                  [image]="'assets/svg/chatbubble-outline.svg'"
                  (buttonClick)="messageGuest()"
                  class="reservation-message-button" />
              }
            </div>

            <div class="resv-stay">
              <div class="resv-stay-col">
                <span class="resv-stay-label">
                  <tidy-image [src]="'assets/svg/log-in-outline.svg'" class="resv-stay-label-icon" />
                  Check-in
                </span>
                <span class="resv-stay-date">{{ rv.checkIn.weekday }}, {{ rv.checkIn.date }}</span>
                @if (guestReservation?.check_in_time) {
                  <span class="resv-stay-time">{{ guestReservation?.check_in_time | parseDateTime:'h:mma' }}</span>
                }
              </div>
              <div class="resv-stay-nights">
                <span class="resv-stay-track"></span>
                @if (rv.nightsLabel) {
                  <span class="resv-stay-nights-pill">
                    <tidy-image [src]="'assets/svg/moon-outline.svg'" class="resv-stay-nights-icon" />
                    {{ rv.nightsLabel }}
                  </span>
                }
                <span class="resv-stay-track"></span>
              </div>
              <div class="resv-stay-col resv-stay-col--right">
                <span class="resv-stay-label">
                  Check-out
                  <tidy-image [src]="'assets/svg/log-out-outline.svg'" class="resv-stay-label-icon" />
                </span>
                <span class="resv-stay-date">{{ rv.checkOut.weekday }}, {{ rv.checkOut.date }}</span>
                @if (guestReservation?.check_out_time) {
                  <span class="resv-stay-time">{{ guestReservation?.check_out_time | parseDateTime:'h:mma' }}</span>
                }
              </div>
            </div>

            @if (rv.priceLabel || rv.canEditPrice) {
              <div class="resv-price">
                <span class="resv-price-label">
                  <tidy-image [src]="'assets/svg/cash-outline.svg'" class="resv-price-icon" />
                  Total price
                </span>
                <span class="resv-price-value">{{ rv.priceLabel || 'Not set' }}</span>
              </div>
            }

            @if (rv.breakdown; as breakdown) {
              <div class="resv-breakdown">
                @for (row of breakdown.guestRows; track $index) {
                  <div class="resv-breakdown-row">
                    <span>{{ row.label | translate }}</span>
                    <span class="resv-breakdown-value">{{ row.value }}</span>
                  </div>
                }
                @if (breakdown.platformRows.length) {
                  <span class="resv-breakdown-title">{{ breakdown.platformTitle | translate }}</span>
                  @for (row of breakdown.platformRows; track $index) {
                    <div class="resv-breakdown-row">
                      <span>{{ row.label | translate }}</span>
                      <span class="resv-breakdown-value">{{ row.value }}</span>
                    </div>
                  }
                }
              </div>
            }

            @if (rv.payoutLabel) {
              <div class="resv-price resv-price-payout">
                <span class="resv-price-label">Your payout</span>
                <span class="resv-price-value">{{ rv.payoutLabel }}</span>
              </div>
            }

            @if (!isEditingTimes && !isEditingPrice) {
              <div class="resv-foot">
                <span class="resv-foot-item">
                  <tidy-text
                    [actionBody]="'Edit Check-in/Check-out Times'"
                    (action)="startEditTimes()"
                    class="link" />
                </span>
                @if (rv.canEditPrice) {
                  <span class="resv-foot-item">
                    <tidy-text
                      [actionBody]="rv.priceLabel ? 'Edit Price' : 'Add Price'"
                      (action)="startEditPrice()"
                      class="link" />
                  </span>
                }
              </div>
            }
            @if (isEditingPrice) {
              <div [@expandInOut]>
                <tidy-alert>
                  <form [formGroup]="editPriceForm">
                    <tidy-row class="vertical-align-center">
                      <tidy-image [src]="'assets/svg/cash-outline.svg'" class="body-size" />
                      <tidy-text [header]="'Set Price'" />
                    </tidy-row>
                    <tidy-row>
                      <tidy-text [helper]="'What the guest pays for the whole stay. Clear the field to remove the price.'" />
                    </tidy-row>
                    <tidy-row>
                      <tidy-input
                        formControlName="price"
                        [form]="editPriceForm.controls.price"
                        [label]="'Total Price'"
                        [icon]="'assets/svg/cash-outline.svg'"
                        inputMode="numeric"
                        [mask]="'currencyMask'"
                        [errorMessage]="'Enter a price of at least $1.00, or leave it blank.'"
                        [submitted]="priceSubmitted" />
                    </tidy-row>
                    <tidy-row class="extra-top-padding">
                      <tidy-button
                        [text]="'Save Price'"
                        class="primary"
                        [disabled]="!hasPriceChanges"
                        [action]="boundSavePrice" />
                    </tidy-row>
                    <tidy-row style="text-align: center">
                      <tidy-text
                        [actionBody]="'Cancel'"
                        (action)="cancelEditPrice()"
                        class="link" />
                    </tidy-row>
                  </form>
                </tidy-alert>
              </div>
            }
            @if (isEditingTimes) {
              <div [@expandInOut]>
                <tidy-alert>
                  <form [formGroup]="editTimesForm">
                    <tidy-row class="vertical-align-center">
                      <tidy-image [src]="'assets/svg/pencil-outline.svg'" class="body-size" />
                      <tidy-text [header]="'Adjust Times'" />
                    </tidy-row>
                    <tidy-row>
                      <tidy-text [helper]="'Record an early check in or late check out for this guest.'" />
                    </tidy-row>
                    <tidy-row>
                      <tidy-select
                        [label]="'Check In Time'"
                        [icon]="'assets/svg/time.svg'"
                        [form]="editTimesForm.controls.checkInTime"
                        formControlName="checkInTime"
                        [items]="timesItems" />
                    </tidy-row>
                    <tidy-row>
                      <tidy-select
                        [label]="'Check Out Time'"
                        [icon]="'assets/svg/time.svg'"
                        [form]="editTimesForm.controls.checkOutTime"
                        formControlName="checkOutTime"
                        [items]="timesItems" />
                    </tidy-row>
                    <tidy-row class="extra-top-padding">
                      <tidy-button
                        [text]="'Save Times'"
                        class="primary"
                        [disabled]="!hasTimeChanges"
                        [action]="boundSaveTimes" />
                    </tidy-row>
                    <tidy-row style="text-align: center">
                      <tidy-text
                        [actionBody]="'Cancel'"
                        (action)="cancelEditTimes()"
                        class="link" />
                    </tidy-row>
                  </form>
                </tidy-alert>
              </div>
            }

            <div class="inqv-pills">
              @if (rv.guestsLabel) {
                <span class="inqv-pill">
                  <tidy-image [src]="'assets/svg/people-outline.svg'" class="inqv-pill-icon" />
                  {{ rv.guestsLabel }}
                </span>
              }
              @if (address) {
                <span class="inqv-pill">
                  <tidy-image [src]="'assets/svg/home-outline.svg'" class="inqv-pill-icon" />
                  {{ address }}
                </span>
              }
            </div>
            @if (rv.guestBreakdown) {
              <span class="resv-guest-breakdown">{{ rv.guestBreakdown }}</span>
            }

            @if (rv.guestRows.length) {
              <div class="resv-guests">
                @for (guest of rv.guestRows; track guest.id) {
                  <div class="resv-guest-row">
                    <span class="resv-guest-avatar">{{ guest.initials }}</span>
                    <div class="resv-guest-meta">
                      <span class="resv-guest-name">
                        {{ guest.name }}
                        @if (guest.isBooker) {
                          <span class="resv-guest-badge">Booker</span>
                        }
                      </span>
                      @if (guest.email) {
                        <span class="resv-guest-sub">{{ guest.email }}</span>
                      }
                      @if (guest.phone) {
                        <span class="resv-guest-sub">{{ guest.phone | telephone }}</span>
                      }
                    </div>
                  </div>
                }
              </div>
            }

            <div class="resv-foot">
              <span class="resv-foot-item">
                <tidy-image [src]="'assets/svg/book-outline.svg'" class="inqv-pill-icon" />
                <tidy-text
                  [actionBody]="'View Digital Guidebook'"
                  (action)="openGuidebook()"
                  class="link" />
              </span>
              <span class="resv-foot-item resv-foot-item--muted">
                @if (rv.addedByYou) {
                  <tidy-image [src]="'assets/svg/create-outline.svg'" class="inqv-pill-icon" />
                  <tidy-text [helper]="'Added by you'" />
                } @else {
                  <tidy-image [src]="'assets/svg/sync-outline.svg'" class="inqv-pill-icon" />
                  <tidy-text [helper]="('Synced from ' | translate) + rv.sourceName" /><span>&nbsp;</span>
                  <tidy-text
                    [actionBody]="'integration'"
                    (action)="goToIntegrations()"
                    class="link" />
                }
              </span>
            </div>

            @if (detail?.source_data) {
              <tidy-row class="no-padding source-data-link-row">
                <div class="source-data-link" (click)="toggleSourceData('reservation')">
                  <tidy-text [body]="'View source data'" />
                  <ion-icon
                    [name]="isSourceDataExpanded('reservation') ? 'chevron-up' : 'chevron-down'"
                    class="source-data-icon" />
                </div>
              </tidy-row>
              <tidy-row class="source-data-content"
                [class.expanded]="isSourceDataExpanded('reservation')"
                [@slideAnimation]="isSourceDataExpanded('reservation') ? 'expanded' : 'collapsed'">
                <div class="source-data-inner">
                  @for (sourceItem of detail?.source_data | keyvalue; track sourceItem; let i = $index) {
                    <div [class]="'container bullet-item'" style="max-inline-size: 80%;">
                      <tidy-text [body]="'• ' + sourceItem.key + ': ' + utils.formatSourceDataValue(sourceItem.value)" />
                    </div>
                  }
                </div>
              </tidy-row>
            }
          </tidy-card>

          <tidy-card class="inqv-card">
            <div class="resv-section-head">
              <span class="resv-section-icon-tile">
                <tidy-image [src]="'assets/svg/flash-outline.svg'" class="resv-section-icon" />
              </span>
              <div class="resv-section-copy">
                <span class="resv-section-title">Auto-Schedule</span>
                <span class="resv-section-hint">Automatically book cleanings around this stay</span>
              </div>
            </div>
            <tidy-row>
              <tidy-checkbox
                [items]="[{value: true, viewValue: 'Enable auto-schedule for this reservation'}]"
                [initValue]="detail?.automatic_booking_settings?.[0]?.is_automatic_booking_enabled"
                (checkedChange)="updateAutomaticBooking()" />
            </tidy-row>
            @if (rv.autoScheduleStatuses.length) {
              <div class="resv-status-chips">
                @for (status of rv.autoScheduleStatuses; track $index) {
                  <span class="resv-status-chip">{{ status }}</span>
                }
              </div>
            }
          </tidy-card>

          @if (doorCodeReady) {
            <tidy-card class="inqv-card">
              <div class="resv-section-head">
                <span class="resv-section-icon-tile">
                  <tidy-image [src]="'assets/svg/key-outline.svg'" class="resv-section-icon" />
                </span>
                <div class="resv-section-copy">
                  <span class="resv-section-title">{{ 'Door Code' | translate }}</span>
                  @if (manualCode) {
                    <span class="resv-section-hint">{{ 'Guests see this code in their guidebook' | translate }}</span>
                  } @else if (lockCodes.length) {
                    <span class="resv-section-hint">{{ 'Smart lock code generated for this stay' | translate }}</span>
                  } @else if (hasSmartLock) {
                    <span class="resv-section-hint">{{ 'Your smart lock gets a code for this stay before check-in' | translate }}</span>
                  } @else {
                    <span class="resv-section-hint">{{ 'No smart lock connected. Add the code you made for this stay and guests see it in their guidebook.' | translate }}</span>
                  }
                </div>
              </div>
              @if (manualCode || lockCodes.length) {
                <div class="resv-kv-list">
                  @if (manualCode) {
                    <div class="resv-kv">
                      <span class="resv-kv-label">{{ 'Your code' | translate }}</span>
                      <span class="resv-kv-value resv-lock-value">
                        <span class="resv-lock-code">{{ manualCode.code }}</span>
                        <tidy-text
                          [actionBody]="'Copy'"
                          (action)="copyLockCode(manualCode.code)"
                          class="link" />
                      </span>
                    </div>
                  }
                  @for (item of lockCodes; track item.code) {
                    <div class="resv-kv" [class.resv-kv--replaced]="manualCode">
                      <span class="resv-kv-label">{{ item.deviceName }}</span>
                      <span class="resv-kv-value resv-lock-value">
                        <span class="resv-lock-code">{{ item.code }}</span>
                        <tidy-text
                          [actionBody]="'Copy'"
                          (action)="copyLockCode(item.code)"
                          class="link" />
                      </span>
                    </div>
                    @if (item.validity) {
                      <span class="resv-lock-validity">{{ item.validity }}</span>
                    }
                  }
                  @if (manualCode && lockCodes.length) {
                    <span class="resv-lock-validity">{{ 'Guests see your code instead of the smart lock’s. The code on the lock does not change.' | translate }}</span>
                  }
                </div>
              }
              @if (isEditingDoorCode) {
                <form [formGroup]="doorCodeForm" class="resv-door-code-form">
                  <tidy-input
                    formControlName="code"
                    [form]="doorCodeForm.controls.code"
                    [label]="'Door code'"
                    [icon]="'assets/svg/key-outline.svg'"
                    [errorMessage]="'Enter a code of up to 20 letters or numbers.'"
                    [submitted]="doorCodeSubmitted" />
                  @if (hasSmartLock) {
                    <tidy-text [helper]="'Guests will see this code instead of the one your smart lock makes. It does not change the code on the lock.'" />
                  }
                  <div class="resv-door-code-actions">
                    <tidy-button
                      [text]="'Save Code'"
                      class="primary"
                      [action]="boundSaveDoorCode" />
                    <tidy-text
                      [actionBody]="'Cancel'"
                      (action)="cancelEditDoorCode()"
                      class="link" />
                  </div>
                </form>
              } @else {
                <div class="resv-foot">
                  @if (manualCode) {
                    <span class="resv-foot-item">
                      <tidy-text [actionBody]="'Edit Code'" (action)="startEditDoorCode()" class="link" />
                    </span>
                    <span class="resv-foot-item">
                      <tidy-text [actionBody]="'Remove Code'" (action)="removeDoorCode()" class="link" />
                    </span>
                  } @else {
                    <span class="resv-foot-item">
                      <tidy-text
                        [actionBody]="hasSmartLock ? 'Use a Different Code' : 'Add Code'"
                        (action)="startEditDoorCode()"
                        class="link" />
                    </span>
                  }
                </div>
              }
            </tidy-card>
          }

          @if (isLoadingBlockedData) {
            <tidy-row>
              <ion-row>
                <ion-col>
                  <tidy-row>
                    <ion-skeleton-text
                      animated
                    style="width: 180px; height: 24px; border-radius: 6px;" />
                  </tidy-row>
                  <tidy-row>
                    <ion-skeleton-text
                      animated
                    style="width: 100%; height: 80px; border-radius: 6px;" />
                  </tidy-row>
                </ion-col>
              </ion-row>
            </tidy-row>
          }
          @if (blockingJobs.length > 0 && !isLoadingBlockedData) {
            <tidy-row>
              <tidy-text [header]="'Blocking Jobs'" />
            </tidy-row>
            @for (item of blockingJobs; track item) {
              <tidy-card>
                <tidy-card-button
                  (action)="goToJobPage(item)"
                  class="no-below-stroke no-padding">
                  <ion-row>
                    <tidy-row class="vertical-align-center no-padding">
                      <tidy-image
                        [src]="item?.status?.icon || item?.icon"
                        style="width: 30px" />
                    </tidy-row>
                    <ion-col>
                      <tidy-row>
                        <tidy-text [header]="item?.service_name | titlecase" />
                      </tidy-row>
                      @if (!item?.parsedRequestTimes) {
                        <tidy-row class="no-padding">
                          <tidy-text
                    [body]="(item?.startDate | parseDateTime: 'ddd') + ' ' + (item?.startDate | parseDateTime: 'MMM d') + (' at ' | translate) + item?.time" />
                        </tidy-row>
                      }
                      @if (item?.parsedRequestTimes) {
                        <tidy-row class="no-padding">
                          <tidy-text [body]="item?.parsedRequestTimes" />
                        </tidy-row>
                      }
                    </ion-col>
                  </ion-row>
                </tidy-card-button>
              </tidy-card>
            }
          }
          @if (calendarReservations.length > 0 && !isLoadingBlockedData) {
            <tidy-row>
              <tidy-text [header]="'Overlapping Reservations'" />
            </tidy-row>
            @for (item of calendarReservations; track item) {
              <tidy-alert>
                <tidy-card-button (action)="goToReservation(item)" class="no-below-stroke no-padding">
                  <ion-row>
                    <tidy-row class="vertical-align-center no-padding">
                      <tidy-image
                        [src]="item?.icon" style="width: 30px" />
                    </tidy-row>
                    <ion-col>
                      <tidy-row>
                        <tidy-text
                          [header]="(item?.isCheckIn ? 'Check In' : 'Check Out') | titlecase" />
                      </tidy-row>
                      <tidy-row class="no-padding">
                        <tidy-text
                    [body]="('Check in: ' | translate) + ' ' + (item?.startDate | parseDateTime: 'ddd') + ' ' + (item?.startDate | parseDateTime: 'MMM d') + (' at ' | translate) + item?.checkInTime" />
                      </tidy-row>
                      <tidy-row class="no-padding">
                        <tidy-text
                    [body]="('Check out: ' | translate) + ' ' + (item?.endDate | parseDateTime: 'ddd') + ' ' + (item?.endDate | parseDateTime: 'MMM d') + (' at ' | translate) + item?.checkOutTime" />
                      </tidy-row>
                    </ion-col>
                  </ion-row>
                </tidy-card-button>
              </tidy-alert>
            }
          }

          @if (!isLoadingBlockedData) {
            <tidy-card class="inqv-card">
              <div class="resv-section-head" [class.resv-section-head--tight]="customFields.length === 0">
                <span class="resv-section-icon-tile">
                  <tidy-image [src]="'assets/svg/list-outline.svg'" class="resv-section-icon" />
                </span>
                <div class="resv-section-copy">
                  <span class="resv-section-title">Custom Fields</span>
                  @if (customFields.length === 0) {
                    <span class="resv-section-hint">No custom fields added</span>
                  }
                </div>
                @if (customFields.length > 0) {
                  <tidy-text
                    [actionBody]="'Edit'"
                    (action)="editCustomFields()"
                    class="link resv-section-action" />
                } @else {
                  <tidy-text
                    [actionBody]="'Create'"
                    (action)="addCustomField()"
                    class="link resv-section-action" />
                }
              </div>
              @if (customFields.length > 0) {
                <div class="resv-kv-list">
                  @for (item of customFields; track item) {
                    <div class="resv-kv">
                      <span class="resv-kv-label">{{ item?.name }}</span>
                      <span class="resv-kv-value">{{ item?.value || 'N/A' }}</span>
                    </div>
                  }
                </div>
              }
            </tidy-card>
          }

          <div class="inqv-actions inqv-actions--delete">
            <tidy-text
              [body]="rv.deleteLabel"
              (action)="deleteReservation()"
              class="link red" />
          </div>
        </div>
      }
    } @else {
      <div class="inqv">
        <tidy-card class="inqv-card">
          <div class="inqv-head">
            <ion-skeleton-text [animated]="true" class="res-sk-avatar" />
            <div class="inqv-head-meta res-sk-meta">
              <ion-skeleton-text [animated]="true" class="res-sk-line res-sk-line--title" />
              <ion-skeleton-text [animated]="true" class="res-sk-line res-sk-line--sub" />
            </div>
          </div>
          <div class="res-sk-stay">
            <div class="res-sk-stay-col">
              <ion-skeleton-text [animated]="true" class="res-sk-line res-sk-line--tiny" />
              <ion-skeleton-text [animated]="true" class="res-sk-line res-sk-line--date" />
            </div>
            <ion-skeleton-text [animated]="true" class="res-sk-pill res-sk-pill--nights" />
            <div class="res-sk-stay-col res-sk-stay-col--right">
              <ion-skeleton-text [animated]="true" class="res-sk-line res-sk-line--tiny" />
              <ion-skeleton-text [animated]="true" class="res-sk-line res-sk-line--date" />
            </div>
          </div>
          <div class="inqv-pills">
            <ion-skeleton-text [animated]="true" class="res-sk-pill" />
            <ion-skeleton-text [animated]="true" class="res-sk-pill res-sk-pill--wide" />
          </div>
        </tidy-card>
        <tidy-card class="inqv-card">
          <div class="res-sk-section">
            <ion-skeleton-text [animated]="true" class="res-sk-tile" />
            <div class="res-sk-meta">
              <ion-skeleton-text [animated]="true" class="res-sk-line res-sk-line--short" />
              <ion-skeleton-text [animated]="true" class="res-sk-line res-sk-line--sub" />
            </div>
          </div>
        </tidy-card>
        <tidy-card class="inqv-card">
          <div class="res-sk-section">
            <ion-skeleton-text [animated]="true" class="res-sk-tile" />
            <div class="res-sk-meta">
              <ion-skeleton-text [animated]="true" class="res-sk-line res-sk-line--short" />
              <ion-skeleton-text [animated]="true" class="res-sk-line res-sk-line--sub" />
            </div>
          </div>
        </tidy-card>
      </div>
    }
  </tidy-wrapper>
</ng-template>

@if (!isRightSideContent) {
  <app-scroll-wrapper [templateContent]="content" />
}

@if (isRightSideContent) {
  <ion-content class="ion-content-right-column">
    <ng-container *ngTemplateOutlet="content" />
  </ion-content>
}

<app-inquiry-decline-modal
  [inquiry]="decliningInquiry"
  [busy]="inquiryBusy"
  (dismissed)="closeInquiryDecline()"
  (submitted)="submitInquiryDecline($event)" />
